import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Menu, Item } from 'react-contexify';
import 'react-contexify/dist/ReactContexify.css';
import { setObjectFit } from '../../../base/conference';
import { openDialog } from '../../../base/dialog';
import { updateSettings } from '../../../base/settings';
import { VolumeControlDialog } from '../../../video-menu/components/web';
import { isLocalTrackMuted } from '../../../base/tracks';
import { MEDIA_TYPE } from '../../../base/media';
import { Icon, IconSwitchCamera, IconContain, IconCover, IconVolume, IconUserPinned, IconModeratorPin } from '../../../base/icons';
import { pinParticipant, SET_MODERATOR_PIN, isLocalParticipantModerator, checkModeratorPinRateLimit } from '../../../base/participants';
import { NOTIFICATION_TYPE, showNotification } from '../../../notifications';
/**
 * LocalVideoContextMenu - A React functional component that renders a context menu
 * for local participant video thumbnail using react-contextify.
 *
 * @param {Object} props - Component props.
 * @param {string} props.participantId - The local participant's ID.
 * @param {string} props.videoSpanId - The video container span ID for the context menu selector.
 * @param {(val: boolean) => void} props.setFlipX - Function to set the flipX state.
 * @param {boolean} props.showParticipantName - Whether to show participant name at the top (for large video).
 * @returns {JSX.Element} The context menu component.
 */
export function LocalVideoContextMenu({ participantId, videoSpanId, setFlipX, showParticipantName = false }) {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    // Get state from Redux
    const { objectFit } = useSelector((state) => state['features/base/conference']);
    const { localFlipX } = useSelector((state) => state['features/base/settings']);
    const localParticipant = useSelector((state) => state['features/base/participants'].find((p) => p.local));
    const isLocalModerator = useSelector(isLocalParticipantModerator);
    const conference = useSelector((state) => state['features/base/conference'].conference);
    const tracks = useSelector((state) => state['features/base/tracks']);
    const isVideoMuted = isLocalTrackMuted(tracks, MEDIA_TYPE.VIDEO);
    // Menu ID for this context menu
    const menuId = `context-menu-${videoSpanId}`;
    // Handler functions
    const handleFlip = useCallback((e) => {
        var _a, _b;
        (_b = (_a = e === null || e === void 0 ? void 0 : e.event) === null || _a === void 0 ? void 0 : _a.stopPropagation) === null || _b === void 0 ? void 0 : _b.call(_a);
        const newFlipX = !localFlipX;
        setFlipX(newFlipX);
        dispatch(updateSettings({ localFlipX: newFlipX }));
    }, [dispatch, localFlipX]);
    const handleObjectFit = useCallback((e) => {
        var _a, _b;
        (_b = (_a = e === null || e === void 0 ? void 0 : e.event) === null || _a === void 0 ? void 0 : _a.stopPropagation) === null || _b === void 0 ? void 0 : _b.call(_a);
        const currentObjectFit = objectFit || 'cover';
        dispatch(setObjectFit(currentObjectFit === 'cover' ? 'contain' : 'cover'));
    }, [dispatch, objectFit]);
    const handleVolumeControl = useCallback((e) => {
        var _a, _b;
        (_b = (_a = e === null || e === void 0 ? void 0 : e.event) === null || _a === void 0 ? void 0 : _a.stopPropagation) === null || _b === void 0 ? void 0 : _b.call(_a);
        dispatch(openDialog(VolumeControlDialog, { participantID: participantId }));
    }, [dispatch, participantId]);
    const handlePin = useCallback((e) => {
        var _a, _b;
        (_b = (_a = e === null || e === void 0 ? void 0 : e.event) === null || _a === void 0 ? void 0 : _a.stopPropagation) === null || _b === void 0 ? void 0 : _b.call(_a);
        dispatch(pinParticipant(participantId, !(localParticipant === null || localParticipant === void 0 ? void 0 : localParticipant.pinned)));
    }, [dispatch, participantId, localParticipant === null || localParticipant === void 0 ? void 0 : localParticipant.pinned]);
    const participantModeratorPin = useCallback((e) => {
        var _a, _b;
        (_b = (_a = e === null || e === void 0 ? void 0 : e.event) === null || _a === void 0 ? void 0 : _a.stopPropagation) === null || _b === void 0 ? void 0 : _b.call(_a);
        if (!conference) {
            return;
        }
        if (checkModeratorPinRateLimit(dispatch, t)) {
            return;
        }
        const myId = conference.myUserId();
        const isModeratorPinned = localParticipant && localParticipant.moderatorPin;
        const pinAction = isModeratorPinned ? 'unpin' : 'pin';
        conference.sendMessage({
            type: SET_MODERATOR_PIN,
            participant_id: participantId,
            senderId: myId,
            action: pinAction
        });
        const notificationKey = pinAction === 'pin'
            ? t('videothumbnail.pinByModerator')
            : t('videothumbnail.unpinByModerator');
        dispatch(showNotification({
            appearance: NOTIFICATION_TYPE.NORMAL,
            titleKey: notificationKey,
            descriptionKey: t('videothumbnail.moderatorPinDiscription')
        }, 3000));
    }, [dispatch, localParticipant, conference, participantId, t]);
    return (React.createElement(Menu, { id: menuId },
        showParticipantName && (React.createElement(Item, { disabled: true },
            React.createElement("span", { style: { fontWeight: 'bold', fontSize: '14px', color: '#fff', pointerEvents: 'none' } }, String(t('me')).toUpperCase()))),
        !isVideoMuted && (React.createElement(Item, { onClick: handleFlip },
            React.createElement("span", { className: 'popupmenu__icon' },
                React.createElement(Icon, { src: IconSwitchCamera })),
            String(t('videothumbnail.flip')))),
        !isVideoMuted && (React.createElement(Item, { onClick: handleObjectFit },
            React.createElement("span", { className: 'popupmenu__icon' },
                React.createElement(Icon, { src: objectFit === 'cover' ? IconContain : IconCover })),
            React.createElement("span", null, objectFit === 'cover' ? String(t('tooltip.switchToContain')) : String(t('tooltip.switchToCover'))))),
        React.createElement(Item, { onClick: handlePin },
            React.createElement("span", { className: 'popupmenu__icon' },
                React.createElement(Icon, { src: IconUserPinned })),
            React.createElement("span", null, (localParticipant === null || localParticipant === void 0 ? void 0 : localParticipant.pinned) ? String(t('videothumbnail.unpinMe')) : String(t('videothumbnail.pinMe')))),
        isLocalModerator && (React.createElement(Item, { onClick: participantModeratorPin },
            React.createElement("span", { className: 'popupmenu__icon' },
                React.createElement(Icon, { src: IconModeratorPin })),
            React.createElement("span", null, (localParticipant === null || localParticipant === void 0 ? void 0 : localParticipant.moderatorPin) ? String(t('videothumbnail.unpinEveryone')) : String(t('videothumbnail.pinEveryone'))))),
        React.createElement(Item, { onClick: handleVolumeControl },
            React.createElement("span", { className: 'popupmenu__icon' },
                React.createElement(Icon, { src: IconVolume })),
            String(t('volumeSlider.label')))));
}
/**
 * Helper function to show context menu for local video
 * @param {MouseEvent} event - The mouse event that triggered the context menu
 * @param {string} videoSpanId - The video container span ID
 */
export function showLocalVideoContextMenu(event, videoSpanId) {
    var _a;
    event.preventDefault();
    (_a = event.stopPropagation) === null || _a === void 0 ? void 0 : _a.call(event);
    // Import contextMenu from react-contexify dynamically
    import('react-contexify').then(({ contextMenu }) => {
        contextMenu.show({
            id: `context-menu-${videoSpanId}`,
            event: event
        });
    });
}
